// ink-component
import React, { ReactNode, useEffect, useMemo, ChangeEvent, useReducer } from 'react';
import shortid from 'shortid';

import TableWrapper from './TableWrapper/TableWrapper';
import TableHeadRow from './TableHeadRow/TableHeadRow';
import Headers from './Headers/Headers';
import ColGroup from './ColGroup/ColGroup';

import { bemHelper, injectChild } from '../../helpers';
import {
  hasRibbon,
  hasStripe,
  isTwiddle,
  getHeadersState,
  defineHeaders,
  getToggledArray,
  childrenWithTrigger,
} from './helpers/helpers';
import useWindowWidth from '../../hooks/useWindowWidth';
import useDeepCompareEffect from '../../hooks/useDeepCompareEffect';

import getGlobalProps from '../../helpers/constants/global-props';
import useDeprecatedWarning from '../../hooks/useDeprecatedWarning';

import TableContext from './TableContext/TableContext';
import tableReducer from './TableContext/tableReducer';

export type Header =
  | ReactNode
  | {
      label: ReactNode;
      sortable: boolean;
      indent: 1 | 2 | 3 | 4;
      order: 'ASC' | 'DESC';
      right: boolean;
      checkbox: boolean;
      colSpan?: number;
      help: ReactNode;
      freeze: boolean;
    };

export interface TableProps {
  /** Unique id to identify the table. Connects child components to the table. */
  id?: string;
  /** Headers array used to populate the headers */
  headers?: Header[];
  /** Array of selected indexes - for external control */
  selected?: number[];
  /** Array of data used to populate the Table rows. Should be treated in the
   * `renderRow` prop. Should not be used with the `children` prop. */
  data?: any[];
  /** RenderProps callback that receives a single `data` item and it's index.
   * Must return a node that is a TableBodyRow with TableCells inside. It functions similarly
   * with the old DataToRow function prop.
   * NOTE: the `isOpen` property for Twiddles, does NOT work with this approach. */
  renderRow: (data: any, index: number) => ReactNode;
  /** Defines if the Table is selectable. Note that you need to pass an unique `id`
   * if there's multiple Tables in a single page. This prop does not work with content as `children` */
  selectable?: 'all' | 'single';
  /** onSelect callback. Passes the selected data item or an array of selected items.
   * This callback does not support tables using children - pass the data through props */
  onSelect?: (data: any) => void;
  /** onSelectAll callback. Passes the data array.
   * This callback does not support tables using children - pass the data through props */
  onSelectAll: (data: any) => void;
  /** Modifier for mini Tables */
  mini?: boolean;
  /** Modifier for hover Tables */
  hover?: boolean;
  /** Sort callback. Passes back the header object and the index clicked */
  /** Modifier for fixed Tables */
  fixed?: boolean;
  /** Sort callback. Passes back the header object and the index in question */
  sort?: (header: Header, index: number) => void;
  /** Modifier for vertical padding */
  density?: 'low' | 'high';
  /** Table contents. The Table can also work with children.
   * NOTE: This prop should not be used with `data` */
  children?: any;
  /** Array of column widths. Can be pixels, percentages or numbers -
   * which are evaluated to percentages */
  widths?: (string | number)[];
  /** Defines if the table will be horizontal scrollable or not. */
  hasScroll?: boolean;
  /** Defines if the table will have the necessary modifier for ribbons */
  hasRibbons?: boolean;
  /** Defines if the table will have the necessary modifier for stripes */
  hasStripes?: boolean;
  /** Defines if the headers will be sticky to the top of the window
   * as the user scrolls down. */
  stickyHeaders?: boolean;
  /** Defines if the table headers should wrap into multiple lines.
   * This prop can be particularly useful if you need narrow column widths
   * and you have long header labels. */
  wrapHeaders?: boolean;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const globalProps = getGlobalProps({ deprecated: true, inkSass: true });

/**
 * @categories table
 * @purpose Turns complex data structures into custom tables
 * @deprecated This component is deprecated and replaced by [NewTable](https://ink.carta.com/components/NewTable/)
 * @status deprecated
 * @replacementComponent NewTable
 * @dateOfDeletion December 1st, 2023
 */
function Table({ data = [], headers = [], ...props }: TableProps) {
  useDeprecatedWarning({ componentName: 'Table', substitute: 'NewTable' });

  const initialState = {
    toggled: [],
    selected: props.selected || [],
    headers: getHeadersState(headers),
  };

  const [state, dispatch] = useReducer(tableReducer, initialState);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const toggleable = useMemo<boolean>(() => isTwiddle({ ...props, data }), [data, props.children]);
  const { isMobile } = useWindowWidth();

  const injectRow = (child: any, index: number, checkToggleState = true) => {
    const props: { key: number; rowIndex: number; isOpen?: boolean } = {
      key: index,
      rowIndex: index,
    };
    if (checkToggleState) {
      props.isOpen = state.toggled[index];
    }
    return injectChild(child, props);
  };

  const renderRows = (checkToggleState = true): ReactNode =>
    data.map((d, index) => injectRow(props.renderRow(d, index), index, checkToggleState));

  const defineChildren = useMemo(
    (): any => (props.children ? props.children : renderRows(false)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, props.children, props.renderRow],
  );

  useDeepCompareEffect(() => {
    const dataExists = data && data.length;
    if (dataExists || props.children) {
      dispatch({
        type: 'setToggled',
        payload: getToggledArray(defineChildren),
      });
    }
  }, [data, props.children]);

  useEffect(() => {
    if (props.selected) {
      dispatch({
        type: 'setSelected',
        payload: props.selected,
      });
    }
  }, [props.selected]);

  useDeepCompareEffect(() => {
    dispatch({
      type: 'setHeaders',
      payload: getHeadersState(headers),
    });
  }, [headers]);

  const toggleAll = () => {
    const children = defineChildren;
    const shouldOpenAll = state.toggled.filter(Boolean).length !== childrenWithTrigger(children);

    const nextToggled = React.Children.map(children, child => {
      if (child && child.props && child.props.trigger) {
        return shouldOpenAll;
      }
      return false;
    });

    dispatch({
      type: 'setToggled',
      payload: nextToggled,
    });
  };

  const toggle = (isOpen: boolean, index: number) => {
    const nextToggled = state.toggled.map((item, i) => {
      if (index === i) {
        return isOpen;
      }
      return item;
    });

    dispatch({
      type: 'setToggled',
      payload: nextToggled,
    });
  };

  const defineTableClassName = (): string => {
    const b = bemHelper();

    return b('ink-table', {
      mini: props.mini,
      fixed: props.fixed || hasStripe(defineChildren),
      ribbon: props.hasRibbons || hasRibbon(defineChildren),
      scroll: props.hasScroll || isMobile,
      stripe: props.hasStripes || hasStripe(defineChildren),
    });
  };

  const toggleActiveHeader = (header, index: number) => {
    const newHeaders = defineHeaders(state.headers, index);

    if (props.sort && header.sortable) {
      props.sort(newHeaders && newHeaders[index], index);
    }

    dispatch({
      type: 'setHeaders',
      payload: newHeaders,
    });
  };

  const isSelected = (index: number): boolean => {
    if (props.selectable) {
      return state.selected.includes(index);
    }
    return false;
  };

  const selectAll = (e: ChangeEvent<HTMLInputElement>) => {
    let nextSelected: number[] = [];
    const childrenCount = React.Children.count(props.children);
    if (e.target.checked) {
      if (childrenCount) {
        for (let i = 0; i < childrenCount; i += 1) {
          nextSelected.push(i);
        }
      } else {
        props.onSelectAll(data);
        nextSelected = data.map((d, i) => i);
      }
    } else {
      props.onSelectAll([]);
    }
    dispatch({
      type: 'setSelected',
      payload: nextSelected,
    });
  };

  const selectRow = (index: number) => {
    let nextSelected: number[] = [];

    if (props.selectable === 'single') {
      props.onSelect && props.onSelect(data[index]);
      nextSelected = [index];
    }
    if (props.selectable === 'all') {
      if (isSelected(index)) {
        props.onSelect && props.onSelect(data.filter((d, i) => state.selected.filter(i => i !== index).includes(i)));
        nextSelected = state.selected.filter(i => i !== index);
      } else {
        props.onSelect && props.onSelect(data.filter((d, i) => state.selected.includes(i) || i === index));
        nextSelected = [...state.selected, index];
      }
    }

    dispatch({
      type: 'setSelected',
      payload: nextSelected,
    });
  };

  const renderChildren = (): ReactNode => {
    return React.Children.map(props.children, (child, index) => {
      if (child) {
        return injectRow(child, index);
      }
      return null;
    });
  };

  const value = {
    tableId: props.id,
    mini: props.mini,
    hover: props.hover,
    density: props.density,
    selectable: props.selectable,
    hasScroll: props.hasScroll,
    selectRow,
    toggle,
    toggleable,
    ...state,
  };

  return (
    <TableContext.Provider value={value}>
      <TableWrapper hasScroll={props.hasScroll || isMobile}>
        <table id={props.id} className={defineTableClassName()} data-testid={props['data-testid']} {...globalProps}>
          {!!(props.widths && props.widths.length) && <ColGroup widths={props.widths} />}
          {!!headers.length && (
            <TableHeadRow sticky={props.stickyHeaders}>
              <Headers
                id={props.id}
                data={data}
                selectable={props.selectable}
                wrapHeaders={props.wrapHeaders}
                selected={state.selected}
                headers={state.headers}
                toggled={state.toggled}
                toggleable={toggleable}
                selectAll={selectAll}
                toggleAll={toggleAll}
                defineChildren={defineChildren}
                toggleActiveHeader={toggleActiveHeader}
              />
            </TableHeadRow>
          )}
          {!!data.length && props.renderRow && <tbody>{renderRows()}</tbody>}
          {props.children && <tbody>{renderChildren()}</tbody>}
        </table>
      </TableWrapper>
    </TableContext.Provider>
  );
}

Table.defaultProps = {
  id: shortid.generate(),
  headers: [],
  selected: undefined,
  data: [],
  renderRow: undefined,
  selectable: undefined,
  onSelect: () => {},
  onSelectAll: () => {},
  mini: false,
  hover: false,
  fixed: false,
  sort: undefined,
  density: undefined,
  children: undefined,
  widths: [],
  hasScroll: false,
  hasRibbons: false,
  hasStripes: false,
  stickyHeaders: false,
  wrapHeaders: false,
};

Table.displayName = 'Table';

export default Table;
